import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ModuleGuard from "@/components/ModuleGuard";
import AccessDenied from "@/pages/AccessDenied";
import { SiteProvider } from "@/context/SiteContext";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Home from "@/pages/Home";
import ModuleHub from "@/pages/ModuleHub";
import MaterialEntry from "@/pages/MaterialEntry";
import Inventory from "@/pages/Inventory";
import Suppliers from "@/pages/Suppliers";
import Sites from "@/pages/Sites";
import Reports from "@/pages/Reports";
import DeliveryNotes from "@/pages/DeliveryNotes";
import DeliveryNotesIndex from "@/pages/DeliveryNotesIndex";
import Contracts from "@/pages/Contracts";
import ContractsIndex from "@/pages/ContractsIndex";
import ContractDetail from "@/pages/ContractDetail";
import Purchase from "@/pages/Purchase";
import RFQ from "@/pages/RFQ";
import RFQIndex from "@/pages/RFQIndex";
import RFQForm from "@/pages/RFQForm";
import RFQOffers from "@/pages/RFQOffers";
import RFQOffersIndex from "@/pages/RFQOffersIndex";
import TeklifList from "@/pages/TeklifList";
import TeklifEditor from "@/pages/TeklifEditor";
import PurchaseReports from "@/pages/PurchaseReports";
import PurchaseReportsIndex from "@/pages/PurchaseReportsIndex";
import RfqReports from "@/pages/RfqReports";
import Employees from "@/pages/Employees";
import Teams from "@/pages/Teams";
import TeamsIndex from "@/pages/TeamsIndex";
import Timesheet from "@/pages/Timesheet";
import HRReport from "@/pages/HRReport";
import HRRecipients from "@/pages/HRRecipients";
import Alerts from "@/pages/Alerts";
import Users from "@/pages/Users";
import Backups from "@/pages/Backups";
import RolesPermissions from "@/pages/RolesPermissions";
import ProjectTypes from "@/pages/ProjectTypes";
import ProjectStructure from "@/pages/ProjectStructure";
import WBSIndex from "@/pages/WBSIndex";
import WBSStructure from "@/pages/WBSStructure";
import ProjectProgressIndex from "@/pages/ProjectProgressIndex";
import ProjectProgress from "@/pages/ProjectProgress";
import WorkItemsIndex from "@/pages/WorkItemsIndex";
import WorkItems from "@/pages/WorkItems";
import ResponsibilityRulesIndex from "@/pages/ResponsibilityRulesIndex";
import ResponsibilityRules from "@/pages/ResponsibilityRules";
import ProjectMahalYapisi from "@/pages/ProjectMahalYapisi";
import BlockWBS from "@/pages/BlockWBS";
import BlockDailyProduction from "@/pages/BlockDailyProduction";
import BlockMaterialBudget from "@/pages/BlockMaterialBudget";
import ProjectGantt from "@/pages/ProjectGantt";
import ProjectGanttIndex from "@/pages/ProjectGanttIndex";
import DailyReport from "@/pages/DailyReport";
import ExpenseNew from "@/pages/ExpenseNew";
import ExpenseList from "@/pages/ExpenseList";
import ExpenseReports from "@/pages/ExpenseReports";
import ExpenseCategories from "@/pages/ExpenseCategories";
import ExpenseClaims from "@/pages/ExpenseClaims";
import ExpenseClaimForm from "@/pages/ExpenseClaimForm";
import ExpenseApprovals from "@/pages/ExpenseApprovals";
import BudgetExpenseDefinitions from "@/pages/BudgetExpenseDefinitions";
import BudgetIncomeDefinitions from "@/pages/BudgetIncomeDefinitions";
import BudgetCBS from "@/pages/BudgetCBS";
import BudgetCashFlow from "@/pages/BudgetCashFlow";
import BudgetProfitLoss from "@/pages/BudgetProfitLoss";
import BudgetAccounts from "@/pages/BudgetAccounts";
import SubFirms from "@/pages/SubFirms";
import SubFirmsIndex from "@/pages/SubFirmsIndex";
import SubContracts from "@/pages/SubContracts";
import SubContractsIndex from "@/pages/SubContractsIndex";
import SubProgress from "@/pages/SubProgress";
import SubProgressIndex from "@/pages/SubProgressIndex";
import SubDeductions from "@/pages/SubDeductions";
import SubDeductionsIndex from "@/pages/SubDeductionsIndex";
import SubAttendance from "@/pages/SubAttendance";
import SubPayments from "@/pages/SubPayments";
import SubPaymentsHub from "@/pages/SubPaymentsHub";
import SubSalaryPayments from "@/pages/SubSalaryPayments";
import SubEvaluation from "@/pages/SubEvaluation";
import SubReports from "@/pages/SubReports";
import ChangePassword from "@/pages/ChangePassword";
import AcceptInvite from "@/pages/AcceptInvite";
import AuditLog from "@/pages/AuditLog";
import PublicConfirmation from "@/pages/PublicConfirmation";
import FinancialControl from "@/pages/FinancialControl";
import GayriResmiKasa from "@/pages/GayriResmiKasa";
import OrtaklarCarisi from "@/pages/OrtaklarCarisi";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-xs uppercase tracking-[0.25em] font-bold text-slate-500">Yükleniyor...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <ModuleGuard>{children}</ModuleGuard>;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "yonetici") return <Navigate to="/" replace />;
  return children;
}

/**
 * Hard-coded yetkili e-posta guard'ı. Kullanıcı whitelist'te değilse
 * ana sayfaya yönlendirir (modülün varlığı bile gizli kalır).
 */
function FinancialGuard({ children }) {
  const { financialAccess, loading } = useAuth();
  if (loading) return null;
  if (!financialAccess?.has_access) return <Navigate to="/" replace />;
  return children;
}

/**
 * Gayri Resmi Kasa Guard — ikinci gizli modül. Yetkisiz görürse ana sayfaya.
 */
function KasaGuard({ children }) {
  const { kasaAccess, loading } = useAuth();
  if (loading) return null;
  if (!kasaAccess?.has_access) return <Navigate to="/" replace />;
  return children;
}

function OrtakGuard({ children }) {
  const { ortakAccess, loading } = useAuth();
  if (loading) return null;
  if (!ortakAccess?.has_access) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={loading ? null : (user ? <Navigate to="/" replace /> : <Login />)} />
      <Route path="/davet/:token" element={<AcceptInvite />} />
      <Route path="/teyit/:token" element={<PublicConfirmation />} />
      <Route path="/" element={<Protected><Home /></Protected>} />
      <Route path="/erisim-yok" element={<Protected><AccessDenied /></Protected>} />
      <Route path="/modul/:slug" element={<Protected><ModuleHub /></Protected>} />
      <Route path="/stok-yonetimi" element={<Protected><Dashboard /></Protected>} />
      <Route path="/malzeme-girisi" element={<Protected><MaterialEntry /></Protected>} />
      <Route path="/stok" element={<Protected><Inventory /></Protected>} />
      <Route path="/firmalar" element={<Protected><Suppliers /></Protected>} />
      <Route path="/santiyeler" element={<Protected><Sites /></Protected>} />
      <Route path="/raporlar" element={<Protected><Reports /></Protected>} />
      <Route path="/irsaliye-arsivi" element={<Protected><DeliveryNotesIndex /></Protected>} />
      <Route path="/irsaliye-arsivi/:siteId" element={<Protected><DeliveryNotes /></Protected>} />
      <Route path="/sozlesmeler" element={<Protected><ContractsIndex /></Protected>} />
      <Route path="/sozlesmeler/detay/:id" element={<Protected><ContractDetail /></Protected>} />
      <Route path="/sozlesmeler/:siteId" element={<Protected><Contracts /></Protected>} />
      <Route path="/satin-alma" element={<Protected><Purchase /></Protected>} />
      <Route path="/satin-alma/rapor" element={<Protected><PurchaseReportsIndex /></Protected>} />
      <Route path="/satin-alma/rapor/satin-alma" element={<Protected><PurchaseReports /></Protected>} />
      <Route path="/satin-alma/rapor/teklif" element={<Protected><RfqReports /></Protected>} />
      <Route path="/teklif-talepleri" element={<Protected><RFQIndex /></Protected>} />
      <Route path="/teklif-talepleri/santiye/:sid" element={<Protected><RFQ /></Protected>} />
      <Route path="/teklif-talepleri/yeni" element={<Protected><RFQForm /></Protected>} />
      <Route path="/teklif-talepleri/:id" element={<Protected><RFQForm /></Protected>} />
      <Route path="/gelen-teklifler" element={<Protected><RFQOffersIndex /></Protected>} />
      <Route path="/gelen-teklifler/:sid" element={<Protected><RFQOffers /></Protected>} />
      <Route path="/teklifler" element={<Protected><TeklifList /></Protected>} />
      <Route path="/teklifler/yeni" element={<Protected><TeklifEditor /></Protected>} />
      <Route path="/teklifler/:id" element={<Protected><TeklifEditor /></Protected>} />
      <Route path="/personel/calisanlar" element={<Protected><Employees /></Protected>} />
      <Route path="/personel/ekipler" element={<Protected><TeamsIndex /></Protected>} />
      <Route path="/personel/ekipler/:siteId" element={<Protected><Teams /></Protected>} />
      <Route path="/personel/puantaj" element={<Protected><Timesheet /></Protected>} />
      <Route path="/personel/rapor" element={<Protected><HRReport /></Protected>} />
      <Route path="/kullanicilar/hr-rapor-alicilari" element={<Protected><HRRecipients /></Protected>} />
      <Route path="/uyarilar" element={<Protected><Alerts /></Protected>} />
      <Route path="/sifre-degistir" element={<Protected><ChangePassword /></Protected>} />
      <Route path="/kullanicilar" element={<Protected><AdminOnly><Users /></AdminOnly></Protected>} />
      <Route path="/yedekler" element={<Protected><AdminOnly><Backups /></AdminOnly></Protected>} />
      <Route path="/roller-izinler" element={<Protected><AdminOnly><RolesPermissions /></AdminOnly></Protected>} />
      <Route path="/ayarlar/proje-tipleri" element={<Protected><AdminOnly><ProjectTypes /></AdminOnly></Protected>} />
      <Route path="/proje/mahal-yapisi" element={<Protected><ProjectMahalYapisi /></Protected>} />
      <Route path="/proje/:siteId/yapisi" element={<Protected><ProjectStructure /></Protected>} />
      <Route path="/proje/wbs" element={<Protected><WBSIndex /></Protected>} />
      <Route path="/proje/:siteId/wbs" element={<Protected><WBSStructure /></Protected>} />
      <Route path="/proje/ilerleme" element={<Protected><ProjectProgressIndex /></Protected>} />
      <Route path="/proje/:siteId/ilerleme" element={<Protected><ProjectProgress /></Protected>} />
      <Route path="/proje/work-items" element={<Protected><WorkItemsIndex /></Protected>} />
      <Route path="/proje/:siteId/work-items" element={<Protected><WorkItems /></Protected>} />
      <Route path="/proje/sorumluluk-kurallari" element={<Protected><ResponsibilityRulesIndex /></Protected>} />
      <Route path="/proje/:siteId/sorumluluk-kurallari" element={<Protected><ResponsibilityRules /></Protected>} />
      <Route path="/proje/:siteId/blok/:blockId/wbs" element={<Protected><BlockWBS /></Protected>} />
      <Route path="/proje/:siteId/blok/:blockId/imalat" element={<Protected><BlockDailyProduction /></Protected>} />
      <Route path="/proje/:siteId/blok/:blockId/butce" element={<Protected><BlockMaterialBudget /></Protected>} />
      <Route path="/proje/gantt" element={<Protected><ProjectGanttIndex /></Protected>} />
      <Route path="/proje/:siteId/gantt" element={<Protected><ProjectGantt /></Protected>} />
      <Route path="/proje/:siteId/gun-sonu" element={<Protected><DailyReport /></Protected>} />
      <Route path="/masraf/yeni" element={<Protected><ExpenseNew /></Protected>} />
      <Route path="/masraf/duzenle/:eid" element={<Protected><ExpenseNew /></Protected>} />
      <Route path="/masraf/liste" element={<Protected><ExpenseList /></Protected>} />
      <Route path="/masraf/talep-formlari" element={<Protected><ExpenseClaims /></Protected>} />
      <Route path="/masraf/talep/:cid" element={<Protected><ExpenseClaimForm /></Protected>} />
      <Route path="/masraf/onaylar" element={<Protected><AdminOnly><ExpenseApprovals /></AdminOnly></Protected>} />
      <Route path="/masraf/rapor" element={<Protected><ExpenseReports /></Protected>} />
      <Route path="/masraf/kategoriler" element={<Protected><AdminOnly><ExpenseCategories /></AdminOnly></Protected>} />
      {/* Bütçe Yönetimi (Masraf Bildirimi altında) — placeholder sayfalar */}
      <Route path="/butce/giderler" element={<Protected><BudgetExpenseDefinitions /></Protected>} />
      <Route path="/butce/gelirler" element={<Protected><BudgetIncomeDefinitions /></Protected>} />
      <Route path="/butce/kirinim" element={<Protected><BudgetCBS /></Protected>} />
      <Route path="/butce/kar-zarar" element={<Protected><BudgetProfitLoss /></Protected>} />
      <Route path="/butce/hesaplar" element={<Protected><BudgetAccounts /></Protected>} />
      <Route path="/butce/nakit-akis" element={<Protected><BudgetCashFlow /></Protected>} />
      <Route path="/taseron/firmalar" element={<Protected><SubFirmsIndex /></Protected>} />
      <Route path="/taseron/firmalar/:siteId" element={<Protected><SubFirms /></Protected>} />
      <Route path="/taseron/sozlesmeler" element={<Protected><SubContractsIndex /></Protected>} />
      <Route path="/taseron/sozlesmeler/:siteId" element={<Protected><SubContracts /></Protected>} />
      <Route path="/taseron/hakedis" element={<Protected><SubProgressIndex /></Protected>} />
      <Route path="/taseron/hakedis/:siteId" element={<Protected><SubProgress /></Protected>} />
      <Route path="/taseron/kesintiler" element={<Protected><SubDeductionsIndex /></Protected>} />
      <Route path="/taseron/kesintiler/:siteId" element={<Protected><SubDeductions /></Protected>} />
      <Route path="/taseron/puantaj" element={<Protected><SubAttendance /></Protected>} />
      <Route path="/taseron/odemeler" element={<Protected><SubPaymentsHub /></Protected>} />
      <Route path="/taseron/odemeler/maas" element={<Protected><SubSalaryPayments /></Protected>} />
      <Route path="/taseron/odemeler/yapilan" element={<Protected><SubPayments /></Protected>} />
      <Route path="/taseron/degerlendirme" element={<Protected><SubEvaluation /></Protected>} />
      <Route path="/taseron/raporlar" element={<Protected><SubReports /></Protected>} />
      <Route path="/islem-gecmisi" element={<Protected><AdminOnly><AuditLog /></AdminOnly></Protected>} />
      <Route path="/finansal-kontrol" element={<Protected><FinancialGuard><FinancialControl /></FinancialGuard></Protected>} />
      <Route path="/gayri-resmi-kasa" element={<Protected><KasaGuard><GayriResmiKasa /></KasaGuard></Protected>} />
      <Route path="/ortaklar-carisi" element={<Protected><OrtakGuard><OrtaklarCarisi /></OrtakGuard></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <SiteProvider>
        <ConfirmProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </ConfirmProvider>
      </SiteProvider>
    </AuthProvider>
  );
}
